import { FC, useCallback, useState, useEffect } from 'react';
import moment from 'moment';
import { DatepickerSimple, DatePickerSimpleProps } from '../DatePickerSimple';
import styles from './InputDateRange.module.scss';
import { Timestamp } from '../types';
import { Position } from './types';
import OutsideClickHandler from '../../../../vendor/OutsideClickHandler';
import { useTranslation } from 'react-i18next';

type Props = {
  triggerVariant?: DatePickerSimpleProps['variant'];
  value?: [Timestamp, Timestamp];
  position?: Position;
  hasInvalidEndDate?: boolean;
  allowBlank?: boolean;
  startYearOffset?: number;
  onChange: (nextValue: [Timestamp, Timestamp]) => void;
};

export const InputDateRange: FC<Props> = ({
  triggerVariant,
  value,
  position,
  hasInvalidEndDate,
  allowBlank,
  startYearOffset,
  onChange,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'DatePicker.InputDateRange' });
  const [start, setStart] = useState<Timestamp | undefined>(() => {
    if (allowBlank) {
      return value?.[0];
    }
    return value?.[0] ?? (moment().subtract(30, 'day').valueOf() as Timestamp);
  });
  const [end, setEnd] = useState<Timestamp | undefined>(() => {
    if (allowBlank) {
      return value?.[1];
    }
    return value?.[1] ?? (moment().valueOf() as Timestamp);
  });

  const handleSelect = useCallback(
    (nextValue: [Timestamp, Timestamp]) => {
      onChange(nextValue);
    },
    [onChange],
  );

  useEffect(() => {
    if (start && end) {
      handleSelect([start, end]);
    }
  }, [start, end]);

  useEffect(() => {
    const [min, max] = value || [];
    setEnd(max);
    setStart(min);
  }, [value]);

  return (
    <OutsideClickHandler onOutsideClick={() => {}}>
      <div className={styles['input-date-range']}>
        <div className={styles['input-date-range-control']}>
          <DatepickerSimple
            variant={triggerVariant}
            title={String(t('startDate', { defaultValue: 'Start Date' }))}
            value={(start || '').toString()}
            selected={start}
            format="timestamp"
            position={position === 'top' ? 'topLeft' : 'bottomLeft'}
            onChange={(e) => setStart(JSON.parse(e.target.value))}
            onBlur={() => {}}
            name="startdate"
            max={end}
            placeholder="From"
            startYearOffset={startYearOffset}
          />

          <span>-</span>

          <DatepickerSimple
            variant={triggerVariant}
            title={String(t('endDate', { defaultValue: 'End Date' }))}
            value={(end || '').toString()}
            selected={end}
            format="timestamp"
            position={position === 'top' ? 'topRight' : 'bottomRight'}
            onChange={(e) => setEnd(JSON.parse(e.target.value))}
            onBlur={() => {}}
            name="endday"
            hasInvalidDate={hasInvalidEndDate}
            min={start}
            placeholder="To"
            startYearOffset={startYearOffset}
          />
        </div>
      </div>
    </OutsideClickHandler>
  );
};
